<script setup>
import { computed, ref } from 'vue';
import useVuelidate from '@vuelidate/core';
import { uploadAttachment } from '../../services/uploadAttachment';

import AppIcon from '../atoms/app/AppIcon.vue';
import FormMessage from '../atoms/form/FormMessage.vue';
import { $unitTestSpy } from '../../utils/unit-test-spy';
import { useApplicationStore } from '../../stores/application-store';
import FileUploadDropZone from './FileUploadDropZone.vue';
import IconListItem from './IconListItem.vue';
import FileListEntry from './fileList/FileListEntry.vue';
import { track, trackVisibility } from '../../utils/tracking';
import { showSnackbar } from '../../utils/snackbar';
import { useBreakpoints } from '../../composables/breakpoint';
import AppBadge from '../atoms/app/AppBadge.vue';

defineProps({
    dropzoneHtmlElement: {
        type: HTMLElement,
    },
});

const MAX_TOTAL_FILESIZE_IN_MB = 12;
const MAX_TOTAL_FILESIZE = MAX_TOTAL_FILESIZE_IN_MB * (10 ** 6);
const MAX_NUMBER_OF_UPLOADED_FILES = 25;
const MIN_FILE_SIZE = 1024; // 1 KB
const MAX_FILE_SIZE = 8 * (10 ** 6); // 8 MB

const uploadingFiles = ref([]);
const currentlyUploading = computed(() => Boolean(uploadingFiles.value.length));
const validationMessages = ref([]);

const { isMobile } = useBreakpoints();

const application = useApplicationStore();

function handleFileValidationError({ message, trackingElementDetail }) {
    validationMessages.value.push(message);
    trackVisibility({
        element: 'AP_F: Invalid File Upload',
        elementDetail: trackingElementDetail,
    });
}

function resetValidationMessages() {
    validationMessages.value = [];
}

function validateFileSize(size, name) {
    const minFileSizeValid = size >= MIN_FILE_SIZE;
    const maxFileSizeValid = size <= MAX_FILE_SIZE;
    if (!maxFileSizeValid) {
        handleFileValidationError({
            message: `Die Dateigröße von "${name}" entspricht nicht den Anforderungen.`,
            trackingElementDetail: 'max-filesize',
        });
    }
    if (!minFileSizeValid) {
        handleFileValidationError({
            message: `Die Datei "${name}" muss eine Mindestgröße von 1 KB haben.`,
            trackingElementDetail: 'min-filesize',
        });
    }

    return maxFileSizeValid && minFileSizeValid;
}

const availableFileTypes = ['PDF', 'DOCX', 'DOC', 'ODT', 'RTF', 'JPG', 'PNG'];
const regexFileTypes = /\.(jpe?g|png|pdf|docx?|odt|rtf)$/i;

function validateFileType(name) {
    const isValidFile = regexFileTypes.test(name);
    if (!isValidFile) {
        handleFileValidationError({
            message: `Das Dateiformat von "${name}" entspricht nicht den Anforderungen.`,
            trackingElementDetail: 'invalid-fileformat',
        });
    }

    return isValidFile;
}

function getFileIndexByName(name) {
    return uploadingFiles.value.findIndex(({ file }) => file.name === name);
}

let abortController;

async function uploadFile({ file }, fromDropzone) {
    $unitTestSpy(); // TODO refactor after Vue 3 upgrade

    // Validate size and type, and start upload if it is correctly
    if (validateFileSize(file.size, file.name) && validateFileType(file.name)) {
        // Initialize upload progress to signal this file is currently uploading
        const index = getFileIndexByName(file.name);
        uploadingFiles.value[index].progress = 0;

        // Create a new form data element for the upload.
        const formData = new FormData();
        formData.append('file', file, file.name);

        abortController = new AbortController();

        // Upload attachment and update the progress.
        const uploadAttachmentResponse = await uploadAttachment(formData, (uploadProgress) => {
            uploadingFiles.value[index].progress = uploadProgress;
        }, abortController)
            .then(response => response)
            .catch((exception) => {
                if (exception.code !== 'ERR_CANCELED') {
                    handleFileValidationError({
                        message: `Die Datei "${file.name}" entspricht nicht den Anforderungen.`,
                        trackingElementDetail: 'wrong-file',
                    });
                }
            });

        // Add attachment to the store.
        if (uploadAttachmentResponse) {
            application.addAttachment(uploadAttachmentResponse);
            track({
                element: 'Document Upload',
                elementDetail: fromDropzone ? 'Drag Drop' : 'File Browser',
            });
        }

        uploadingFiles.value.splice(index, 1);
    } else {
        const index = getFileIndexByName(file.name);
        uploadingFiles.value.splice(index, 1);
    }
}

async function processFileUploadQueue(files, fromDropzone = false) {
    $unitTestSpy('process');
    if (!files.length) {
        uploadingFiles.value = [];
        return;
    }

    // show all files that are to be uploaded
    uploadingFiles.value = files.map(file => ({
        progress: undefined,
        file,
    }));

    // Upload the first file of the queue.
    await uploadFile(uploadingFiles.value[0], fromDropzone);
    files.shift();

    // Do the upload until the queue is empty.
    await processFileUploadQueue(files);
}

function validateMaxAndMinFiles(files, fromDropzone) {
    // Currently uploading files and already uploaded, active files.
    const maxFiles = (application.activeAttachments.length + files) <= MAX_NUMBER_OF_UPLOADED_FILES;
    if (!maxFiles) {
        handleFileValidationError({
            message: `Du kannst nicht mehr als ${MAX_NUMBER_OF_UPLOADED_FILES} Dateien im Anhang haben.`,
            trackingElementDetail: 'max-files',
        });

        if (fromDropzone) {
            showSnackbar({
                text: `Du kannst nicht mehr als ${MAX_NUMBER_OF_UPLOADED_FILES} Unterlagen mitschicken.`,
            });
        }
    }

    return maxFiles && files > 0;
}

async function handleUpload(files, fromDropzone) {
    if (!files?.length) {
        return;
    }

    // Cleanup of old validation messages.
    resetValidationMessages();

    if (validateMaxAndMinFiles(files.length, fromDropzone) && !currentlyUploading.value) {
        // Add Files to the upload queue.
        await processFileUploadQueue(Array.from(files), fromDropzone);
    }
}

const activeAttachmentsCount = computed(() => application.activeAttachments.length +
    uploadingFiles.value.length);
const maxAttachmentsReached = computed(() => ((activeAttachmentsCount.value >= MAX_NUMBER_OF_UPLOADED_FILES) ?
    true : undefined));
const uploadNotPossible = computed(() => maxAttachmentsReached.value || currentlyUploading.value);

const shouldShowActiveAttachments = computed(() => activeAttachmentsCount.value > 0);

const validations = {
    attachments: {
        maxSize: attachments => attachments.reduce((acc, cur) => acc + cur.size, 0) <= MAX_TOTAL_FILESIZE,
        maxNumber: attachments => attachments.length <= MAX_NUMBER_OF_UPLOADED_FILES,
    },
    uploadingFiles: {
        mustNotBeUploading: uploading => !uploading.length,
    },
};

const state = computed(() => ({
    attachments: computed(() => application.activeAttachments),
    uploadingFiles,
}));

const v$ = useVuelidate(validations, state, { $registerAs: 'attachments' });

function updateValidation() {
    resetValidationMessages();
    v$.value.$touch();
}

function abortUpload() {
    abortController?.abort();
}

function removeFileFromQueue(name) {
    const index = getFileIndexByName(name);
    uploadingFiles.value.splice(index, 1);
}
</script>

<template>
    <div class="o-fileUpload k-o-ladder--l">
        <FileUploadDropZone
            v-if="dropzoneHtmlElement"
            :visible=" !uploadNotPossible"
            :target-element="dropzoneHtmlElement"
            @dropped="handleUpload($event, true)"
        >
            <template #title>
                Sonstige Unterlagen hier ablegen
            </template>
            <template #subtitle>
                <div class="flex gap-xs justify-center flex-wrap mt-xs mb-xs">
                    <AppBadge
                        v-for="fileType in availableFileTypes"
                        :key="fileType"
                        outline
                    >
                        {{ fileType }}
                    </AppBadge>
                </div>
                max. 8 MB pro Datei
            </template>
        </FileUploadDropZone>
        <label
            :class="uploadNotPossible ? 'cursor-not-allowed' : 'cursor-pointer'"
            class="block"
            data-qa="upload box"
        >
            <IconListItem
                :muted="uploadNotPossible"
            >
                <template
                    v-if="!isMobile"
                    #icon
                >
                    <div class="bg-white inset-0 w-full h-full flex items-center justify-center">
                        <AppIcon
                            name="plus"
                            size="xl"
                        />
                    </div>
                </template>
                <template #title>
                    <div class="flex flex-col">
                        <div class="">
                            <span class="underline mr-s">Unterlagen hochladen</span>
                            <div class="hidden m:inline-flex gap-x-xs">
                                <AppBadge outline>PDF</AppBadge>
                                <AppBadge outline>DOC</AppBadge>
                                <AppBadge outline>JPG</AppBadge>
                                <AppBadge outline>PNG</AppBadge>
                            </div>
                        </div>
                        <input
                            class="u-screen-reader-only u-screen-reader-only--focusable"
                            type="file"
                            data-qa="file input"
                            :disabled="uploadNotPossible"
                            multiple
                            accept="image/jpeg, image/png, application/pdf,
                                    .doc, .docx, .odt, text/rtf, application/rtf"
                            @change="handleUpload($event.target.files)"
                        >
                    </div>
                </template>
                <template #subtitle>
                    <p>Füge Motivationsschreiben, Zeugnisse oder Sonstiges hinzu (Max. 8 MB pro Datei)</p>
                    <p class="m:hidden"><span class="mr-xs">Empfohlen:</span>
                        <span class="inline-flex gap-x-xs">
                            <AppBadge outline>PDF</AppBadge>
                            <AppBadge outline>DOC</AppBadge>
                            <AppBadge outline>JPG</AppBadge>
                            <AppBadge outline>PNG</AppBadge>
                        </span>
                    </p>
                </template>
            </IconListItem>
        </label>
        <!-- VALIDATION MESSAGES -->
        <FormMessage
            v-if="validationMessages.length"
            type="error"
            data-qa="attachments form message"
            class="o-fileUpload__formMessage"
        >
            <div
                v-for="validationMessage in validationMessages"
                :key="validationMessage"
            >
                {{ validationMessage }}
            </div>
        </FormMessage>
        <FormMessage
            v-if="v$.attachments.maxSize.$invalid"
            type="error"
            data-qa="attachments form message max size"
            class="o-fileUpload__formMessage"
        >
            Die Anhänge dürfen die Maximalgröße von {{ MAX_TOTAL_FILESIZE_IN_MB }} MB nicht überschreiten.
        </FormMessage>
        <div
            v-if="shouldShowActiveAttachments"
            data-qa="active attachments list"
            class="flex flex-col gap-l mt-xl"
        >
            <div class="flex gap-l items-center mb-s">
                <span class="text-s text-gray-600 flex-shrink-0">
                    {{ activeAttachmentsCount }} Unterlage{{ activeAttachmentsCount > 1 ? 'n' : null }}
                    im Anhang (Max. {{ MAX_NUMBER_OF_UPLOADED_FILES }})
                </span>
                <hr class="k-c-divider k-c-divider--fine k-c-divider--full border-0">
            </div>
            <!-- active attachments -->
            <div
                v-for="activeAttachment in application.activeAttachments"
                :key="activeAttachment.uuid ?? activeAttachment.name"
                v-track-visibility="activeAttachment.isProfileDocument ? {
                    element: 'AP_F: Files from profile',
                } : undefined"
                class="o-fileUpload__listEntry"
                data-qa="attachment"
            >
                <FileListEntry
                    :file="activeAttachment"
                    @attachment-changed="updateValidation"
                />
            </div>
            <!-- uploading files -->
            <div
                v-for="uploadingFile in uploadingFiles"
                :key="uploadingFile.file.lastModified"
                class="o-fileUpload__listEntry"
            >
                <FileListEntry
                    :file="uploadingFile.file"
                    :is-currently-uploading="uploadingFile.progress !== undefined"
                    :upload-progress="uploadingFile.progress ?? 0"
                    @abort-upload="abortUpload"
                    @remove-file-from-queue="removeFileFromQueue"
                />
            </div>
        </div>
        <!-- inactive attachments -->
        <div
            v-if="application.inactiveAttachments.length"
            data-qa="inactive attachments list"
            class="flex flex-col gap-l mt-l"
        >
            <hr class="k-c-divider my-s k-c-divider--fine k-c-divider--full border-0">
            <div
                v-for="inactiveAttachment in application.inactiveAttachments"
                :key="inactiveAttachment.uuid"
                class="o-fileUpload__listEntry"
                data-qa="inactive attachment"
            >
                <FileListEntry
                    :file="inactiveAttachment"
                    :disable-attachment-activation="maxAttachmentsReached"
                />
            </div>
        </div>
    </div>
</template>

<style lang="scss">
@use '~@karriereat/global-styles/scss/components/link/mixins' as link;
@use '~@karriereat/global-styles/scss/components/divider';
@use '~@karriereat/global-styles/scss/settings/breakpoint';
@use '~@karriereat/global-styles/scss/settings/spacing';
@use '~@karriereat/global-styles/scss/tools/typo';

$iconHeightAndWidth: 22px;
$fileSizeWidth: 60px;
$fileExtensionWidth: 36px;

.o-fileUpload {
    &__icon {
        width: $iconHeightAndWidth;
        height: $iconHeightAndWidth;
        color: var(--k-gray-800);
    }

    &__text {
        font-weight: 500;
    }

    &__textAndLink {
        margin-top: spacing.$k-spacing--xs;
    }

    &__uploadingFileInfos {
        display: flex;
        flex-direction: row;
        align-items: baseline;
        margin-bottom: -(spacing.$k-spacing--s);
        min-width: 0;
    }

    &__uploadingFileName {
        flex-grow: 1;
        overflow: hidden;
        color: var(--k-gray-500);
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    &__uploadingFileExtension {
        margin-left: spacing.$k-spacing--xs;
        min-width: $fileExtensionWidth;
        text-align: center;
    }

    &__uploadingFileSize {
        @include typo.k-typo-s();
        margin-left: spacing.$k-spacing--xs;
        min-width: $fileSizeWidth;
        text-align: right;
        color: var(--k-gray-400);
        white-space: nowrap;
    }

    &__counter {
        margin-top: spacing.$k-spacing--m;
        margin-left: spacing.$k-spacing--xs;
        color: var(--k-gray-500);
    }

    &__formMessage {
        margin-top: spacing.$k-spacing--s;
        margin-bottom: spacing.$k-spacing--s;
    }
}
</style>
